import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient, useCurrentUser, cn, randomUUID } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { useCoinBalance } from '../../_lib/wallet-api'

const PRESETS = [
  { cny: 10, coin: 1000 },
  { cny: 30, coin: 3000 },
  { cny: 68, coin: 6800, badge: '热门' as const },
  { cny: 128, coin: 12_800 },
  { cny: 328, coin: 32_800 },
  { cny: 648, coin: 64_800, badge: '超值' as const },
]

type CreateOrderResp = {
  order_id: string
  pay_url: string
  amount_cny: number
  amount_coin: number
  expires_at: string
}

export function RechargePage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: wallet } = useCoinBalance(user?.user_id)
  const [amountCny, setAmountCny] = useState<number>(68)
  const [custom, setCustom] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const finalCny = custom ? Number(custom) : amountCny
  const finalCoin = finalCny * 100

  const submit = async () => {
    setBusy(true)
    setError(null)
    // 外链支付新开页面:必须在点击同步阶段开窗,await 响应后再 window.open 会被弹窗拦截
    const payWin = window.open('', '_blank')
    try {
      const res = await apiClient.post<CreateOrderResp>('/gapi/user/recharges', {
        amount_cny_cents: finalCny * 100,
        provider: 'otcbtc',
        idempotency_key: randomUUID(),
      })
      if (payWin) {
        payWin.opener = null
        payWin.location.href = res.pay_url
      } else {
        window.open(res.pay_url, '_blank', 'noopener')
      }
      navigate(`/recharge/callback?order_id=${res.order_id}`)
    } catch (e) {
      payWin?.close()
      setError(e instanceof Error ? e.message : '创建订单失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar title="充值金币" />

        <main data-testid="3mWtc" className="flex flex-1 flex-col gap-5 px-6 pt-4 pb-32">
          <section className="rounded-2xl bg-white p-4">
            <p className="text-[12px] text-[#9CA3AF]">当前余额</p>
            <p className="mt-1 flex items-baseline gap-1 text-[24px] font-bold tabular-nums text-[#1A1A1A]">
              {wallet?.balance ? Number(wallet.balance).toLocaleString() : '0'}
              <span className="text-[12px] font-normal text-[#9CA3AF]">金币</span>
            </p>
          </section>

          <section>
            <p className="mb-3 text-[14px] font-semibold text-[#1A1A1A]">选择金额</p>
            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => {
                const active = !custom && amountCny === p.cny
                return (
                  <button
                    key={p.cny}
                    type="button"
                    onClick={() => {
                      setCustom('')
                      setAmountCny(p.cny)
                    }}
                    className={cn(
                      'relative flex flex-col items-center justify-center rounded-xl py-4',
                      active
                        ? 'border-2 border-[#19caad] bg-white'
                        : 'border border-transparent bg-white',
                    )}
                  >
                    {p.badge && (
                      <span
                        className="absolute -top-1 right-1 rounded-md px-1.5 py-0.5 text-[9px] font-semibold text-white"
                        style={{ background: p.badge === '热门' ? '#FF6B6B' : '#FF8C00' }}
                      >
                        {p.badge}
                      </span>
                    )}
                    <span className="text-[18px] font-bold text-[#1A1A1A]">¥{p.cny}</span>
                    <span className="mt-0.5 text-[11px] text-[#9CA3AF]">{p.coin.toLocaleString()}金币</span>
                  </button>
                )
              })}
            </div>
          </section>

          <section>
            <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">自定义金额</p>
            <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-3">
              <span className="text-[16px] font-semibold text-[#1A1A1A]">¥</span>
              <input
                type="number"
                min={10}
                max={999}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="输入金额"
                className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#B4B7BC]"
              />
              {custom && <span className="text-[12px] text-[#9CA3AF]">≈ {Number(custom) * 100} 金币</span>}
            </div>
          </section>

          <section>
            <p className="mb-3 text-[14px] font-semibold text-[#1A1A1A]">支付方式</p>
            <div className="flex flex-col gap-2">
              <PayRow label="微信/支付宝" color="#09BB07" checked onClick={() => {}} />
            </div>
          </section>

          {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
        </main>

        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-[#F6F7F8] px-6 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={submit}
            disabled={busy || finalCny < 10 || finalCny > 999}
            className="h-12 w-full rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '提交中…' : `立即充值 ¥${finalCny || 0}`}
          </button>
          <p className="mt-1 text-center text-[10px] text-[#9CA3AF]">≈ {finalCoin || 0} 金币</p>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

function PayRow({
  label,
  color,
  checked,
  onClick,
}: {
  label: string
  color: string
  checked: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-14 items-center gap-3 rounded-xl bg-white px-4 text-left active:bg-[#F1F2F4]"
    >
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] text-white"
        style={{ background: color }}
      >
        {label.includes('微信') ? '微' : '支'}
      </span>
      <span className="flex-1 text-[14px] text-[#1A1A1A]">{label}</span>
      <span
        className="flex h-5 w-5 items-center justify-center rounded-full border-2"
        style={{ borderColor: checked ? '#19caad' : '#D1D5DB', background: checked ? '#19caad' : 'transparent' }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.6"><path d="M5 12l5 5 9-12" /></svg>
        )}
      </span>
    </button>
  )
}
